import {
    type ForwardRefRenderFunction,
    type CSSProperties
} from "react";
import type {
    ImageStyle,
    TextStyle,
    ViewStyle
} from "react-native";
import {
    type INCoreUIKitIconCallbackProps
} from "./icon";
import {
    type LocalizeType,
    type LocaleType
} from "./locale";
import {
    type ModalDataType,
    type ModalType
} from "./modal";
import {
    type GapPropagationType,
    type SharpnessType,
    type ThemeConfigs,
    type PaletteType,
    type ThemesType,
    type ThemeType
} from "./theme";
import {
    type ToastPromiseOptionsType,
    type ToastPromiseStateType,
    type ToastConfigType
} from "./toast";
import defaultLocaleJSON from "../variants/locales/default.json";
import defaultThemeJSON from "../variants/themes/default.json";

export type {
    PortalizedComponentProps
} from "./portalize";

export type {
    INCoreUIKitIconCallbackProps,
    INCoreUIKitIconProps,
    NCoreUIKitIcon
} from "./icon";

export type {
    ControlMeasuresConstantType,
    ControlStepDownMeasuresKeys,
    ControlStyleConstantType,
    BreakpointConstantType,
    ThemeContextStateType,
    HitAreaConstantType,
    ThemePreferenceType,
    ControlMeasuresKeys,
    PointerListenerType,
    ThemeContextType,
    ControlSizeType,
    ControlStepType,
    HitAreaMeasures,
    ControlMeasures,
    BreakpointType,
    ControlStyles,
    ThemeConfigs,
    PointerType
} from "./theme";

export type {
    LocalizeContextStateType,
    LocalizeContextType,
    DirectionType
} from "./locale";

export type {
    ToastPromiseOptionsType,
    ToastPromiseStateType,
    GapPropagationType,
    NCoreUIKitConfig,
    SharpnessType,
    ModalDataType,
    LocalizeType,
    PaletteType,
    ThemesType,
    LocaleType,
    ModalType,
    ThemeType
};

export type ActivityStatusType = "online" | "offline" | "busy" | "away" | "idle";

export type PureRNStyles = Omit<ViewStyle & TextStyle & ImageStyle, "position" | "cursor" | "filter">;

export type PureWebStyles = Omit<CSSProperties, "position" | "cursor" | "filter">;

export type AllStyles = PureRNStyles & PureWebStyles & {
    position?: CSSProperties["position"];
    cursor?: CSSProperties["cursor"];
    cornerShape?: string;
    filter?: string;
};

export type RecursiveRecord = {
  [key: string]: string | RecursiveRecord;
};

export interface RefForwardingComponent<T, P = object> extends ForwardRefRenderFunction<T, P> {};

export interface SafePointerEvent {
    clientY: number;
}

export interface SafeWebWindow {
    removeEventListener: (type: "pointercancel" | "pointermove" | "pointerup", listener: (evt: SafePointerEvent) => void) => void;
    addEventListener: (type: "pointercancel" | "pointermove" | "pointerup", listener: (evt: SafePointerEvent) => void) => void;
}

export const safeGlobal = globalThis as unknown as SafeWebWindow;

export type Mutable<T> = {
    -readonly [P in keyof T]: T[P];
};

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends Array<unknown> ? T[P] : T[P] extends object ? DeepPartial<T[P]> : T[P];
};

type NCoreUIKitConfig = ThemesType & LocalizeType & ModalType & ToastConfigType;

declare global {
    namespace NCoreUIKit {
        interface GapPropagationKey extends Record<GapPropagationType, unknown> {}
        interface SharpnessKey extends Record<SharpnessType, unknown> {}
        interface PaletteKey extends Record<PaletteType, unknown> {}
        interface LocaleKey extends Record<LocaleType, unknown> {}
        interface ThemeKey extends Record<ThemeType, unknown> {}

        type IconCallbackProps = INCoreUIKitIconCallbackProps;

        interface ActivePalette {
            controlPaddings: ControlPaddings;
            controlHeights: ControlHeights;
            inlineSpaces: InlineSpaces;
            shapeConfigs: ShapeConfigs;
            touchTargets: TouchTargets;
            avatarSizes: AvatarSizes;
            typography: Typography;
            elevations: Elevations;
            configs: ThemeConfigs;
            durations: Durations;
            iconSizes: IconSizes;
            colors: ThemeTokens;
            shadowAlpha: number;
            radiuses: Radiuses;
            borders: Borders;
            spaces: Spaces;
        }

        interface Palette {
            isNeedAndroidTypographyFixer: boolean;
            typography: TypographyWithVariants;
            palettes: ThemePalettes;
            shapes: Shapes;
        }

        interface PartialPalette {
            typography?: PartialTypographyWithVariants;
            isNeedAndroidTypographyFixer?: boolean;
            palettes?: PartialThemePalettes;
            shapes?: PartialShapes;
        }

        type SpacesWithVariants = Record<GapPropagationType, Spaces>;

        type RadiusesWithVariants = Record<SharpnessType, Radiuses>;

        type ControlHeightsWithVariants = Record<GapPropagationType, ControlHeights>;

        type ControlPaddingsWithVariants = Record<GapPropagationType, ControlPaddings>;

        type ShadowAlphaWithVariants = Record<keyof typeof defaultThemeJSON.shapes.shadowAlpha, number> & Partial<Record<keyof ThemeKey, number>>;

        type Shapes = {
            controlPaddings: ControlPaddingsWithVariants;
            controlHeights: ControlHeightsWithVariants;
            shadowAlpha: ShadowAlphaWithVariants;
            radiuses: RadiusesWithVariants;
            inlineSpaces: InlineSpaces;
            shapeConfigs: ShapeConfigs;
            touchTargets: TouchTargets;
            spaces: SpacesWithVariants;
            avatarSizes: AvatarSizes;
            elevations: Elevations;
            durations: Durations;
            iconSizes: IconSizes;
            borders: Borders;
        };

        type PartialShapes = DeepPartial<Shapes>;

        type DefaultSpaces = Record<keyof typeof defaultThemeJSON.shapes.spaces.compact, number>;

        interface Spaces extends DefaultSpaces {}

        type DefaultBorders = Record<keyof typeof defaultThemeJSON.shapes.borders, number>;

        interface Borders extends DefaultBorders {
        }

        type DefaultInlineSpaces = Record<keyof typeof defaultThemeJSON.shapes.inlineSpaces, number>;

        interface InlineSpaces extends DefaultInlineSpaces {
        }

        type DefaultShapeConfigs = typeof defaultThemeJSON.shapes.shapeConfigs;

        interface ShapeConfigs extends DefaultShapeConfigs {
        }

        type DefaultRadiuses = Record<keyof typeof defaultThemeJSON.shapes.radiuses.rounded, number>;

        interface Radiuses extends DefaultRadiuses {}

        type DefaultControlHeights = Record<keyof typeof defaultThemeJSON.shapes.controlHeights.compact, number>;

        interface ControlHeights extends DefaultControlHeights {}

        type DefaultControlPaddings = Record<keyof typeof defaultThemeJSON.shapes.controlPaddings.compact, number>;

        interface ControlPaddings extends DefaultControlPaddings {}

        type DefaultIconSizes = Record<keyof typeof defaultThemeJSON.shapes.iconSizes, number>;

        interface IconSizes extends DefaultIconSizes {}

        type DefaultAvatarSizes = Record<keyof typeof defaultThemeJSON.shapes.avatarSizes, number>;

        interface AvatarSizes extends DefaultAvatarSizes {}

        type DefaultTouchTargets = Record<keyof typeof defaultThemeJSON.shapes.touchTargets, number>;

        interface TouchTargets extends DefaultTouchTargets {}

        type DefaultDurations = Record<keyof typeof defaultThemeJSON.shapes.durations, number>;

        interface Durations extends DefaultDurations {}

        type DefaultElevations = Record<keyof typeof defaultThemeJSON.shapes.elevations, Array<ElevationLayer>>;

        interface Elevations extends DefaultElevations {}

        interface ElevationLayer {
            offset: number;
            alpha: number;
            blur: number;
        }

        type TypographyWithVariants = Record<GapPropagationType, Typography>;

        type PartialTypographyWithVariants = Partial<Record<GapPropagationType, Partial<Typography>>>;

        type DefaultTypography = Record<keyof typeof defaultThemeJSON.typography.compact, TypographyToken>;

        interface Typography extends DefaultTypography {}

        interface TypographyToken {
            letterSpacing: number;
            fontWeight: string;
            fontFamily: string;
            lineHeight: number;
            fontSize: number;
        }

        type ThemePalettes = Array<ThemePalette>;

        interface ThemePalette {
            themes: Theme;
            name: string;
        }

        type Theme = Record<keyof ThemeKey, ThemeTokens>;

        type PartialThemePalettes = Array<PartialThemePalette>;

        interface PartialThemePalette {
            themes: PartialTheme;
            name: string;
        }

        type PartialTheme = Partial<Record<keyof ThemeKey, PartialThemeTokens>>;

        type ColorsMerged = DefaultSystemColors & DefaultContentColors;

        type ThemeTokens = {
            project: ProjectColorPalette;
            content: ContentColorPalette;
            system: SystemColorPalette;
            chart: ChartColorPalette;
            code: CodeColorPalette;
        };

        type PartialThemeTokens = DeepPartial<ThemeTokens>;

        type DefaultPalette = typeof defaultThemeJSON.palettes[0];

        type DefaultLowColors = typeof defaultThemeJSON.palettes[0]["themes"]["dark"]["system"]["low"];

        interface LowColors extends DefaultLowColors {
        }

        type ProjectData = DefaultPalette["themes"]["dark"]["project"];
        type DefaultProjectColors = ProjectData;

        interface ProjectColorPalette extends DefaultProjectColors {
        }

        type SystemData = DefaultPalette["themes"]["dark"]["system"];
        type DefaultSystemColors = SystemData;

        interface SystemColorPalette extends DefaultSystemColors {
        }

        type ContentData = DefaultPalette["themes"]["dark"]["content"];
        type DefaultContentColors = ContentData;

        interface ContentColorPalette extends DefaultContentColors {
        }

        type CodeData = DefaultPalette["themes"]["dark"]["code"];
        type DefaultCodeColors = CodeData;

        interface CodeColorPalette extends DefaultCodeColors {
        }

        type ChartData = DefaultPalette["themes"]["dark"]["chart"];
        type DefaultChartColors = ChartData;

        interface ChartColorPalette extends DefaultChartColors {
        }

        type ContainerContentColors = DefaultContentColors["container"];
        type BorderContentColors = DefaultContentColors["border"];
        type IconContentColors = DefaultContentColors["icon"];
        type TextContentColors = DefaultContentColors["text"];

        type DisabledSystemColors = DefaultSystemColors["disabled"];
        type StateSystemColors = DefaultSystemColors["state"];

        type Locales = Array<Locale>;

        interface Locale {
            rruleConfig?: GeneratedRRuleConfigs;
            translations?: Translation;
            locale: string;
            isRTL: boolean;
        }

        type DefaultLocalization = typeof defaultLocaleJSON[0];

        type GeneratedRRuleConfigsForReturn = {
            [K in keyof DefaultLocalization["rruleConfig"]]: DefaultLocalization["rruleConfig"][K];
        };

        type GeneratedRRuleConfigs = {
            [K in keyof DefaultLocalization["rruleConfig"]]?: DefaultLocalization["rruleConfig"][K];
        };

        type GeneratedTranslations = {
            [K in keyof DefaultLocalization["translations"]]?: DefaultLocalization["translations"][K];
        };

        interface Translation extends GeneratedTranslations {
        }

        type NavigationRoute = {
            params?: Record<string, unknown>;
            state?: NavigationState;
            name: string;
            key: string;
        };

        type NavigationState = {
            routes: Array<NavigationRoute>;
            index: number;
        };

        type Navigation = {
            addListener: (state: string, callback: () => void) => () => void;
            getRootState: () => NavigationState;
            navigate: (page: string, config?: {
                screen: string
            }) => void;
            getState: () => NavigationState;
            canGoBack: () => boolean;
            goBack: () => void;
        };
    }
}
